import { ActionIcon, Badge, Box, Group, Paper, Popover, Progress, SimpleGrid, Stack, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { TbInfoCircle } from 'react-icons/tb'

import { BillingForecast, DISCOUNT, TARGET_RATIO, ratio } from './billingForecast'
import { formatCzk, formatPercent } from './format'

const ProgressStat = ({ label, value, forecast }: { label: string; value: number; forecast: BillingForecast }) => {
  const { t, i18n } = useTranslation('billing')
  const ofOrder = ratio(value, forecast.order)
  const ofTarget = ratio(value, forecast.target)

  return (
    <Paper p="lg" radius="md">
      <Text size="sm" c="dimmed" mb={6}>
        {label}
      </Text>
      <Text fz={26} fw={700} lh={1.3}>
        {formatCzk(value, i18n.language)}
      </Text>
      <Box pos="relative" mt="md" mb="sm">
        <Progress value={Math.min(ofOrder, 1) * 100} size="lg" aria-label={label} />
        {/* Target marker on the order scale. */}
        <Box
          pos="absolute"
          top={-3}
          left={`${TARGET_RATIO * 100}%`}
          w={2}
          h="calc(100% + 6px)"
          bg="var(--mantine-color-text)"
          style={{ borderRadius: 1 }}
          aria-hidden
        />
      </Box>
      <Text size="xs" c="dimmed">
        {t('summary.ofOrder', { value: formatPercent(ofOrder, i18n.language) })} ·{' '}
        {t('summary.ofTarget', { value: formatPercent(ofTarget, i18n.language) })}
      </Text>
    </Paper>
  )
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <Group justify="space-between" gap="xs" wrap="nowrap">
    <Text size="sm" c="dimmed">
      {label}
    </Text>
    <Text size="sm" fw={500} ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Text>
  </Group>
)

const DetailCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Paper p="md" radius="md">
    <Text size="sm" fw={600} mb="xs">
      {title}
    </Text>
    <Stack gap={4}>{children}</Stack>
  </Paper>
)

export const AssumptionsPopover = ({ timezone }: { timezone: string }) => {
  const { t, i18n } = useTranslation('billing')
  const percent = (value: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 }).format(value)

  return (
    <Popover width={360} position="bottom-start" withArrow shadow="md">
      <Popover.Target>
        <ActionIcon variant="subtle" color="gray" aria-label={t('assumptions.label')}>
          <TbInfoCircle size={18} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            {t('assumptions.title')}
          </Text>
          <Text size="sm">{t('assumptions.forecast')}</Text>
          <Text size="sm">{t('assumptions.target', { ratio: percent(TARGET_RATIO) })}</Text>
          <Text size="sm">
            {t('assumptions.discount', {
              weekdays: DISCOUNT.weekdays.count,
              weekdayFactor: percent(DISCOUNT.weekdays.factor),
              weekendDays: DISCOUNT.weekendDays.count,
              weekendFactor: percent(DISCOUNT.weekendDays.factor),
            })}
          </Text>
          <Text size="sm" c="dimmed">
            {t('assumptions.today', { timezone })}
          </Text>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}

/** "Day X of Y" of the billing period; nothing for an empty history. */
export const PeriodDayBadge = ({ forecast }: { forecast: BillingForecast }) => {
  const { t } = useTranslation('billing')
  if (forecast.totalDays === 0) return null
  const day = Math.min(forecast.elapsedDays + 1, forecast.totalDays)

  return (
    <Badge variant="light" color="gray" size="lg">
      {t('summary.dayOfPeriod', { day, total: forecast.totalDays })}
    </Badge>
  )
}

export const BillingSummary = ({ forecast }: { forecast: BillingForecast }) => {
  const { t, i18n } = useTranslation('billing')
  const czk = (value: number) => formatCzk(value, i18n.language)

  return (
    <Stack gap="sm">
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
        <ProgressStat label={t('summary.billedToDate')} value={forecast.billedToDate} forecast={forecast} />
        <ProgressStat label={t('summary.forecast')} value={forecast.forecast} forecast={forecast} />
        <ProgressStat label={t('summary.discountedForecast')} value={forecast.discountedForecast} forecast={forecast} />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
        <DetailCard title={t('summary.orderAndTarget')}>
          <Row label={t('summary.order')} value={czk(forecast.order)} />
          <Row
            label={t('summary.target', {
              ratio: new Intl.NumberFormat(i18n.language, { style: 'percent' }).format(TARGET_RATIO),
            })}
            value={czk(forecast.target)}
          />
        </DetailCard>
        <DetailCard title={t('summary.dailyAverages')}>
          <Row label={t('summary.weekdayAverage')} value={czk(forecast.weekdayAverage)} />
          <Row label={t('summary.weekendAverage')} value={czk(forecast.weekendAverage)} />
        </DetailCard>
        <DetailCard title={t('summary.fixedBilling')}>
          <Row label={t('summary.planning')} value={czk(forecast.planning)} />
          <Row label={t('summary.development')} value={czk(forecast.development)} />
        </DetailCard>
      </SimpleGrid>
    </Stack>
  )
}

export default BillingSummary
